import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserDropdown from './UserDropdown';
import Sidebar from './Sidebar';
import {
  ComposedChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  FileText,
  ShoppingCart,
  Receipt,
  Wallet,
  ArrowUp,
  ArrowDown,
  Users,
  Clock,
  PlusCircle,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  FileClock
} from 'lucide-react';
import dashboardApi from '../api/dashboardApi';

const currency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const shortCurrency = (amount) => {
  const n = Number(amount) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)} K`;
  return `₹${n.toFixed(0)}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '0.85rem',
  border: '1px solid #f1f5f9',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)'
};

const cardTitleStyle = { fontSize: '1rem', fontWeight: 600, color: '#1f2937', margin: 0 };

const QUOTATION_STATUS_COLORS = { draft: '#f59e0b', sent: '#3b82f6', hold: '#f97316', accepted: '#16a34a' };
const CHART_SERIES_LABELS = {
  quotations: 'Quotations', sales: 'Sales', invoices: 'Invoices',
  revenue_received: 'Received', revenue_not_received: 'Not Received'
};
const SALES_OVERVIEW_COLORS = {
  quotations: '#3b82f6', sales: '#16a34a', invoices: '#7c3aed',
  revenue_received: '#059669', revenue_not_received: '#ef4444'
};
const isRevenueKey = (key) => key === 'revenue_received' || key === 'revenue_not_received';

/** Two-decimal percentage with trailing zeros trimmed (67.45, 32.5, 100). */
const formatPercent = (value) => `${Number((Number(value) || 0).toFixed(2))}%`;

/** Bars use gradient `fill="url(#...)"` for visual polish, but recharts' default tooltip
 * swatch can't render a gradient URL as a color (every series ends up looking identical
 * in the hover box) — so this draws its own dot per series from a known solid color. */
const SalesOverviewTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem 1rem', boxShadow: '0 8px 20px -8px rgba(0,0,0,0.2)' }}>
      <p style={{ margin: 0, marginBottom: '0.4rem', fontWeight: 600, color: '#1f2937', fontSize: '0.8rem' }}>{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#4b5563', marginTop: '0.2rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: SALES_OVERVIEW_COLORS[entry.dataKey], flexShrink: 0 }} />
          <span>{CHART_SERIES_LABELS[entry.dataKey] || entry.dataKey}:</span>
          <strong style={{ color: '#1f2937' }}>{isRevenueKey(entry.dataKey) ? shortCurrency(entry.value) : entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

/** Animates a number from its previous value up/down to `target` with an ease-out curve. */
function useCountUp(target, duration = 700) {
  const [display, setDisplay] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (target == null || Number.isNaN(target)) return undefined;
    const start = prevTarget.current;
    const delta = target - start;
    const startTime = performance.now();
    let raf;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(start + delta * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevTarget.current = target;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return display;
}

const KpiCard = ({ icon: Icon, gradient, glow, label, rawValue, formatValue = (v) => Math.round(v), change, loading, delay = 0, breakdown }) => {
  const animated = useCountUp(loading ? 0 : (rawValue ?? 0));
  const hasChange = typeof change === 'number' && !Number.isNaN(change);
  const isFlat = hasChange && change === 0;
  const isUp = hasChange && change > 0;
  const trendColor = !hasChange || isFlat ? '#9ca3af' : isUp ? '#16a34a' : '#dc2626';
  const showValue = loading || rawValue === null || rawValue === undefined;

  return (
    <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', animationDelay: `${delay}s` }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        <div
          className="dash-kpi-icon"
          style={{ padding: '0.75rem', background: gradient, borderRadius: '0.65rem', marginRight: '1rem', display: 'flex', boxShadow: `0 8px 16px -6px ${glow}` }}
        >
          <Icon style={{ color: 'white' }} size={22} />
        </div>
        <div>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>{label}</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
            {showValue ? '-' : formatValue(animated)}
          </p>
        </div>
      </div>
      {breakdown && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
          {breakdown.map((b) => (
            <div key={b.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: '#6b7280' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: b.color, display: 'inline-block', marginRight: '0.4rem' }} />
                {b.label}
              </span>
              <strong style={{ color: '#1f2937' }}>{b.value}</strong>
            </div>
          ))}
        </div>
      )}
      {change !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: trendColor }}>
          {hasChange && !isFlat && (isUp ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
          {hasChange ? (
            <>
              <span style={{ marginLeft: isFlat ? 0 : '0.25rem', fontWeight: 600 }}>{formatPercent(Math.abs(change))}</span>
              <span style={{ marginLeft: '0.35rem', color: '#9ca3af' }}>vs last month</span>
            </>
          ) : (
            <span style={{ color: '#9ca3af' }}>No data last month to compare</span>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await dashboardApi.getOverview();
        if (resp.data && resp.data.success) {
          setOverview(resp.data.data);
        } else {
          setError(resp.data?.message || 'Failed to load dashboard');
        }
      } catch (e) {
        setError('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, []);

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const quotationStatusData = (overview?.quotationStatus || []).map((s) => ({
    ...s,
    color: QUOTATION_STATUS_COLORS[s.status] || '#94a3b8'
  }));
  const totalQuotationsForPie = quotationStatusData.reduce((sum, s) => sum + s.count, 0);

  const invoiceSummary = overview?.invoiceSummary || { paid: { count: 0, amount: 0 }, pending: { count: 0, amount: 0 }, draft: { count: 0, amount: 0 } };

  return (
    <div className="main-container" style={{ display: 'flex', height: '100vh', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <style>
        {`
          @keyframes dashFadeInUp {
            from { opacity: 0; transform: translateY(14px); }
            /* Ends at "none", not "translateY(0)" — a non-none transform value
               creates a new CSS stacking context even at zero displacement, which
               otherwise traps the user dropdown's z-index inside the header card's
               own stacking context and lets later sibling cards paint over it. */
            to { opacity: 1; transform: none; }
          }
          @keyframes dashWave {
            0%, 60%, 100% { transform: rotate(0deg); }
            10%, 30% { transform: rotate(14deg); }
            20% { transform: rotate(-8deg); }
            40% { transform: rotate(-4deg); }
            50% { transform: rotate(10deg); }
          }
          .dash-card {
            animation: dashFadeInUp 0.5s ease both;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .dash-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 16px 28px -12px rgba(15, 23, 42, 0.18);
          }
          .dash-card:hover .dash-kpi-icon {
            transform: scale(1.08) rotate(-4deg);
          }
          .dash-kpi-icon {
            transition: transform 0.25s ease;
          }
          .dash-wave {
            display: inline-block;
            transform-origin: 70% 70%;
            animation: dashWave 2.4s ease-in-out infinite;
          }
          .dash-quick-action {
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
          }
          .dash-quick-action:hover {
            transform: translateY(-2px);
            border-color: #c7d2fe !important;
            background: linear-gradient(135deg, #eff6ff, #f5f3ff) !important;
            box-shadow: 0 10px 18px -10px rgba(79, 70, 229, 0.35);
          }
          .dash-reminder-item {
            transition: background-color 0.2s ease, transform 0.2s ease;
          }
          .dash-reminder-item:hover {
            background-color: #fef9ec !important;
            transform: translateX(3px);
          }
          .dash-row {
            transition: background-color 0.15s ease;
          }
          .dash-row:hover {
            background-color: #f8fafc;
          }
          .dash-legend-dot {
            transition: transform 0.2s ease;
          }
          .dash-legend-item:hover .dash-legend-dot {
            transform: scale(1.4);
          }
        `}
      </style>

      <Sidebar />

      <div className="main-content" style={{ flex: 1, padding: '2rem', minWidth: 0, height: '100vh', overflowY: 'auto' }}>
        {/* Header banner */}
        <div
          className="dash-card"
          style={{
            background: 'linear-gradient(135deg, #eef2ff 0%, #e0f2fe 55%, #ecfdf5 100%)',
            border: '1px solid #e0e7ff',
            borderRadius: '1rem',
            padding: '1.75rem 2rem',
            marginBottom: '1.5rem',
            position: 'relative',
            zIndex: 5
          }}
        >
          {/* Decorative layer only — clipped to the card's rounded corners without
              clipping the user dropdown menu, which needs to overflow this card. */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '1rem', overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{
              position: 'absolute', top: '-50px', right: '-40px', width: '180px', height: '180px',
              borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.22), transparent 70%)'
            }} />
            <div style={{
              position: 'absolute', bottom: '-60px', right: '160px', width: '140px', height: '140px',
              borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.15), transparent 70%)'
            }} />
          </div>
          <div className="header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.35rem' }}>
                {greeting}, {user?.first_name || user?.username}! <span className="dash-wave">👋</span>
              </h2>
              <p style={{ color: '#4b5563', margin: 0 }}>
                Let's keep your business moving forward.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <p style={{ color: '#4b5563', fontSize: '0.875rem', margin: 0, whiteSpace: 'nowrap' }}>
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
              <div className="user-dropdown">
                <UserDropdown />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ padding: '1rem', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="dashboard-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <KpiCard
            icon={FileText}
            gradient="linear-gradient(135deg, #60a5fa, #2563eb)"
            glow="rgba(37, 99, 235, 0.35)"
            label="Total Quotations"
            rawValue={overview?.quotations_count}
            change={loading ? undefined : (overview ? overview.quotations_change ?? null : undefined)}
            loading={loading}
            delay={0}
          />
          <KpiCard
            icon={ShoppingCart}
            gradient="linear-gradient(135deg, #4ade80, #16a34a)"
            glow="rgba(22, 163, 74, 0.35)"
            label="Total Sales"
            rawValue={overview?.sale_orders_count}
            change={loading ? undefined : (overview ? overview.sale_orders_change ?? null : undefined)}
            loading={loading}
            delay={0.05}
          />
          <KpiCard
            icon={Receipt}
            gradient="linear-gradient(135deg, #a78bfa, #7c3aed)"
            glow="rgba(124, 58, 237, 0.35)"
            label="Total Invoices"
            rawValue={overview?.proforma_invoices_count}
            change={loading ? undefined : (overview ? overview.proforma_invoices_change ?? null : undefined)}
            loading={loading}
            delay={0.1}
          />
          <KpiCard
            icon={Wallet}
            gradient="linear-gradient(135deg, #fb923c, #ea580c)"
            glow="rgba(234, 88, 12, 0.35)"
            label="Total Revenue"
            rawValue={overview?.proforma_total_amount}
            formatValue={shortCurrency}
            change={loading ? undefined : (overview ? overview.revenue_change ?? null : undefined)}
            breakdown={[
              { label: 'Received', value: shortCurrency(overview?.revenue_received_amount), color: SALES_OVERVIEW_COLORS.revenue_received },
              { label: 'Not Received', value: shortCurrency(overview?.revenue_not_received_amount), color: SALES_OVERVIEW_COLORS.revenue_not_received }
            ]}
            loading={loading}
            delay={0.15}
          />
          {user?.role === 'admin' && (
            <KpiCard
              icon={Users}
              gradient="linear-gradient(135deg, #38bdf8, #0284c7)"
              glow="rgba(2, 132, 199, 0.35)"
              label="Total Users"
              rawValue={overview?.users_count}
              loading={loading}
              delay={0.2}
            />
          )}
        </div>

        {/* Charts Row */}
        <div className="dashboard-charts-row" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', animationDelay: '0.2s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={cardTitleStyle}>Sales Overview</h3>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '0.25rem 0.65rem', borderRadius: '999px' }}>This Year</span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={overview?.monthlyOverview || []} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradQuotations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.75} />
                  </linearGradient>
                  <linearGradient id="gradSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity={1} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0.75} />
                  </linearGradient>
                  <linearGradient id="gradInvoices" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={1} />
                    <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.75} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="count" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis yAxisId="revenue" orientation="right" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={shortCurrency} />
                <Tooltip content={<SalesOverviewTooltip />} />
                <Legend
                  payload={[
                    { value: 'Quotations', type: 'square', color: SALES_OVERVIEW_COLORS.quotations },
                    { value: 'Sales', type: 'square', color: SALES_OVERVIEW_COLORS.sales },
                    { value: 'Invoices', type: 'square', color: SALES_OVERVIEW_COLORS.invoices },
                    { value: 'Received', type: 'line', color: SALES_OVERVIEW_COLORS.revenue_received },
                    { value: 'Not Received', type: 'line', color: SALES_OVERVIEW_COLORS.revenue_not_received }
                  ]}
                />
                <Bar yAxisId="count" dataKey="quotations" fill="url(#gradQuotations)" radius={[3, 3, 0, 0]} barSize={10} animationDuration={900} />
                <Bar yAxisId="count" dataKey="sales" fill="url(#gradSales)" radius={[3, 3, 0, 0]} barSize={10} animationDuration={900} animationBegin={100} />
                <Bar yAxisId="count" dataKey="invoices" fill="url(#gradInvoices)" radius={[3, 3, 0, 0]} barSize={10} animationDuration={900} animationBegin={200} />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue_received"
                  stroke={SALES_OVERVIEW_COLORS.revenue_received}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: SALES_OVERVIEW_COLORS.revenue_received, strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: SALES_OVERVIEW_COLORS.revenue_received, stroke: 'white', strokeWidth: 2 }}
                  animationDuration={1200}
                />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue_not_received"
                  stroke={SALES_OVERVIEW_COLORS.revenue_not_received}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: SALES_OVERVIEW_COLORS.revenue_not_received, strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: SALES_OVERVIEW_COLORS.revenue_not_received, stroke: 'white', strokeWidth: 2 }}
                  animationDuration={1200}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', display: 'flex', flexDirection: 'column', animationDelay: '0.25s' }}>
            <h3 style={{ ...cardTitleStyle, marginBottom: '1rem' }}>Quotation Status</h3>
            <div style={{ position: 'relative', flex: 1 }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={quotationStatusData}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={55}
                    outerRadius={80}
                    cornerRadius={6}
                    paddingAngle={3}
                    animationDuration={900}
                  >
                    {quotationStatusData.map((entry) => (
                      <Cell key={entry.status} fill={entry.color} stroke="white" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    // Keep the tooltip above the absolutely-positioned centre
                    // total label below, which otherwise paints over it.
                    wrapperStyle={{ zIndex: 10 }}
                    contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb', backgroundColor: '#fff', boxShadow: '0 8px 20px -8px rgba(0,0,0,0.2)' }}
                    formatter={(value, name, item) => [`${value} (${formatPercent(item?.payload?.percentage)})`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{
                position: 'absolute', top: '46%', left: '50%', transform: 'translate(-50%, -50%)',
                textAlign: 'center', pointerEvents: 'none'
              }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>{totalQuotationsForPie}</p>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Quotations</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', justifyContent: 'center', marginTop: '0.5rem' }}>
              {quotationStatusData.map((entry) => (
                <div key={entry.status} className="dash-legend-item" style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: '#4b5563', cursor: 'default' }}>
                  <span className="dash-legend-dot" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color, display: 'inline-block', marginRight: '0.4rem' }} />
                  {entry.label} {formatPercent(entry.percentage)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reminders / Quick Actions / Invoice Summary Row */}
        <div className="dashboard-widgets-row" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '1.5rem', marginBottom: '1.5rem', alignItems: 'stretch' }}>
          <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', animationDelay: '0.3s' }}>
            <h3 style={{ ...cardTitleStyle, marginBottom: '1rem' }}>Upcoming Reminders</h3>
            {(!overview?.reminders || overview.reminders.length === 0) ? (
              <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{loading ? 'Loading...' : 'No upcoming renewals in the next few days.'}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {overview.reminders.map((r) => (
                  <div key={r.id} className="dash-reminder-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                      <div style={{ padding: '0.5rem', backgroundColor: '#fef3c7', borderRadius: '0.5rem', flexShrink: 0 }}>
                        <Clock size={16} style={{ color: '#d97706' }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.company_name}</p>
                      </div>
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500, flexShrink: 0, marginLeft: '0.5rem' }}>{formatDate(r.due_date)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', animationDelay: '0.35s' }}>
              <h3 style={{ ...cardTitleStyle, marginBottom: '1rem' }}>Quick Actions</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {[
                  { label: 'Create Quotation', icon: PlusCircle, color: '#3b82f6', path: '/quotations' },
                  { label: 'Create Sale Order', icon: ShoppingCart, color: '#16a34a', path: '/sale-orders' },
                  { label: 'Create Invoice', icon: FileText, color: '#7c3aed', path: '/proforma-invoices' },
                  { label: 'Add Contact', icon: UserPlus, color: '#ea580c', path: '/contacts' }
                ].map((action) => (
                  <button
                    key={action.label}
                    className="dash-quick-action"
                    onClick={() => navigate(action.path)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                      backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                      cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, color: '#374151', textAlign: 'left'
                    }}
                  >
                    <action.icon size={18} style={{ color: action.color, flexShrink: 0 }} />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', flex: 1, animationDelay: '0.4s' }}>
              <h3 style={{ ...cardTitleStyle, marginBottom: '1rem' }}>Invoice Summary</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { key: 'paid', label: 'Paid', icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' },
                  { key: 'pending', label: 'Pending', icon: FileClock, color: '#d97706', bg: '#fef3c7' },
                  { key: 'draft', label: 'Draft', icon: AlertCircle, color: '#6b7280', bg: '#f3f4f6' }
                ].map((row) => (
                  <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ padding: '0.4rem', backgroundColor: row.bg, borderRadius: '0.4rem', display: 'flex' }}>
                        <row.icon size={14} style={{ color: row.color }} />
                      </div>
                      <span style={{ fontSize: '0.875rem', color: '#374151' }}>{row.label}</span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>({invoiceSummary[row.key].count})</span>
                    </div>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>{shortCurrency(invoiceSummary[row.key].amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Records Row */}
        <div className="dashboard-recent-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', animationDelay: '0.45s' }}>
            <h3 style={{ ...cardTitleStyle, marginBottom: '1rem' }}>Recent Quotations</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Quotation</th>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Client</th>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Amount</th>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.recentQuotations || []).map((q) => (
                    <tr key={q.id} className="dash-row" style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.6rem 0.25rem', color: '#1f2937', fontWeight: 500, whiteSpace: 'nowrap' }}>{q.quotation_no}</td>
                      <td style={{ padding: '0.6rem 0.25rem', color: '#4b5563' }}>{q.company_name || '-'}</td>
                      <td style={{ padding: '0.6rem 0.25rem', color: '#4b5563', whiteSpace: 'nowrap' }}>{currency(q.grand_total)}</td>
                      <td style={{ padding: '0.6rem 0.25rem' }}>
                        <span style={{
                          padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 500,
                          backgroundColor: `${QUOTATION_STATUS_COLORS[q.status] || '#94a3b8'}22`,
                          color: QUOTATION_STATUS_COLORS[q.status] || '#64748b'
                        }}>
                          {q.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!overview?.recentQuotations || overview.recentQuotations.length === 0) && !loading && (
                    <tr><td colSpan={4} style={{ padding: '1rem 0.25rem', color: '#9ca3af', textAlign: 'center' }}>No quotations yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="dash-card" style={{ ...cardStyle, padding: '1.5rem', animationDelay: '0.5s' }}>
            <h3 style={{ ...cardTitleStyle, marginBottom: '1rem' }}>Recent Sales</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Sale Order</th>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Client</th>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Amount</th>
                    <th style={{ padding: '0.5rem 0.25rem', fontWeight: 500 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.recentSaleOrders || []).map((so) => (
                    <tr key={so.id} className="dash-row" style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.6rem 0.25rem', color: '#1f2937', fontWeight: 500, whiteSpace: 'nowrap' }}>{so.saleorder_no}</td>
                      <td style={{ padding: '0.6rem 0.25rem', color: '#4b5563' }}>{so.company_name || '-'}</td>
                      <td style={{ padding: '0.6rem 0.25rem', color: '#4b5563', whiteSpace: 'nowrap' }}>{currency(so.total_amount)}</td>
                      <td style={{ padding: '0.6rem 0.25rem' }}>
                        <span style={{
                          padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 500,
                          backgroundColor: so.status === 'closed' ? '#dcfce722' : '#dbeafe',
                          color: so.status === 'closed' ? '#16a34a' : '#1d4ed8'
                        }}>
                          {so.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!overview?.recentSaleOrders || overview.recentSaleOrders.length === 0) && !loading && (
                    <tr><td colSpan={4} style={{ padding: '1rem 0.25rem', color: '#9ca3af', textAlign: 'center' }}>No sale orders yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
